import { Button, Collapse, CollapseProps, Flex, Skeleton } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import Icon from 'utils/icon';

// services
import NotificationService from 'core/services/notification';

// models
import { dateFormat } from 'utils/dateFormat';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import InfiniteScroll from 'react-infinite-scroll-component';
import { onDeleteCommittee, onGetCommitteesList } from '../services/committeeServices';
import CommitteeItem from '../components/committeeItem';
import { isRTL } from 'core/services/language';
import { Committee } from '../models/committee';
import StatusComponent from 'components/status/status';
import { StatusColors } from 'utils/statusColors';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

//styles

const panelStyle: React.CSSProperties = {
	padding: '10px 20px',
	marginBottom: 24,
	background: '#fff',
	borderRadius: 'var(--radius)',
	border: 'none',
};

const CommitteesListPage: FunctionComponent = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [items, setItems] = useState<CollapseProps['items']>([]);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [deletedBoardId, setDeletedBoardId] = useState<number | null>(null);

	useEffect(() => {
		getCommittees();
	}, []);

	const getCommittees = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCommitteesList(1);
			setTotalRecords(response.pagination.total);

			setItems(generateCommitteesItems(response.committees));

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async () => {
		try {
			const response = await onGetCommitteesList(currentPage + 1);

			setItems((prevItems) => [...prevItems!, ...generateCommitteesItems(response.committees)!]);

			setCurrentPage(currentPage + 1);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const generateCommitteesItems = (committees: Committee[]): CollapseProps['items'] => {
		return committees.map((committee, index) => ({
			key: committee.id,
			style: panelStyle,
			children: <CommitteeItem committee={committee} refresh={getCommittees} />,
			label: (
				<Flex justify="space-between" align="center">
					<div>
						<Flex gap={100} align="center">
							<h6 className={`mb-0 fw-medium ${committee.status === 'active' ? 'text-success' : committee.status === 'draft' ? '' : 'text-danger'}`}>
								{committee.status === 'draft' ? t('ACTIONS.DRAFT') : ''}{' '}
								{t('ACTIONS.COMMITTEE_NAME', { name: isRTL() ? committee.name.ar : committee.name.en })}
							</h6>

							{committee.status != 'draft' && (
								<StatusComponent
									style={{ padding: '2px 10px', borderRadius: 10 }}
									text={t(`COMMON.${committee.status.toUpperCase()}`)}
									color={StatusColors[committee.status]}
								/>
							)}
						</Flex>

						{committee.status === 'active' && <p className="mt-2 mb-0 text-light">{dateFormat(committee.appointed_at)}</p>}
					</div>

					{committee.status === 'draft' && (
						<Flex gap={20} align="center">
							<ListButton
								type="delete"
								{...suspendedProps}
								onClick={(e) => {
									e.stopPropagation();
									setDeletedBoardId(committee.id);
									setDeleteModalOpen(true);
								}}
							/>

							<SuspendedTooltip>
								<Button
									type="primary"
									size="middle"
									onClick={(e) => {
										e.stopPropagation();
										navigate(`/dashboard/actions/boards/committees/${committee.id}/attach-members`);
									}}>
									{t('COMMON.CONTINUE')}
								</Button>
							</SuspendedTooltip>
						</Flex>
					)}
				</Flex>
			),
		}));
	};

	const deleteHandler = async () => {
		try {
			setIsDeleting(true);

			await onDeleteCommittee(deletedBoardId!);

			setIsDeleting(false);
			setDeleteModalOpen(false);
			getCommittees();
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.COMMITTEE_DELETED_SUCCESSFULLY'));
		} catch (error) {
			setIsDeleting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.COMMITTEE_DELETED_FAILED'));
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.BOARD_COMMITTEES')}
				subTitle={t('ACTIONS.COMMITTEES_DESC')}
				newRequestText={t('ACTIONS.FORM_NEW_COMMITTEE')}
			/>

			{isLoading ? (
				<LoadingComponent />
			) : items!.length === 0 ? (
				<NoData message={t('ACTIONS.NO_COMMITTEE_AVAILABLE')} />
			) : (
				<InfiniteScroll
					dataLength={items!.length}
					next={fetchMoreData}
					hasMore={totalRecords > items!.length}
					loader={<Skeleton.Button block active style={{ height: 84, borderRadius: 'var(--radius)' }} />}>
					{
						<Collapse
							items={items}
							bordered={false}
							style={{ background: '#f9fafb' }}
							expandIconPosition="end"
							expandIcon={(props) => (
								<Icon
									style={{ transform: props.isActive ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }}
									name="rightArrow"
									height={16}
									width={16}
									color="var(--primary)"
								/>
							)}
						/>
					}
				</InfiniteScroll>
			)}

			{/* DELETE MODAL */}
			<ConfirmModalComponent
				open={deleteModalOpen}
				title={t('ACTIONS.DELETE_COMMITTEE')}
				subTitle={t('ACTIONS.CONFIRM_DELETE_COMMITTEE')}
				confirmText={t('COMMON.DELETE')}
				noteType="red"
				note={t('ACTIONS.COMMITTEE_DELETE_NOTE')}
				confirmColor="red"
				onDiscard={() => setDeleteModalOpen(false)}
				onConfirm={deleteHandler}
				isLoading={isDeleting}
			/>
		</>
	);
};

export default CommitteesListPage;
